import {type Theme, styled, CSSObject} from "@mui/material/styles"
import {Typography} from "@mui/material"
import merge from "lodash/merge"
import {ChipStyles} from "./models"
import {removeUnsetProps} from "../utils"

export const MockSearchBannerWrapper = styled("div")({
    display: "flex",
    maxWidth: "1200px",
})

export const StyledChip = styled("a", {shouldForwardProp: prop => !String(prop).startsWith("$")})<{
    $chipStyles: ChipStyles
    $isStructuredBrowsingChip?: boolean
    $isConversational?: boolean
}>(
    ({
        theme,
        $chipStyles,
        $isStructuredBrowsingChip,
        $isConversational,
    }: {
        theme: Theme
        $chipStyles: ChipStyles
        $isStructuredBrowsingChip?: boolean
        $isConversational?: boolean
    }) => {
        const override = removeUnsetProps({
            backgroundColor: $chipStyles?.backgroundColor,
            border: $chipStyles?.border,
            borderBottom: $chipStyles?.borderBottom,
            borderColor: $chipStyles?.borderColor,
            borderRadius: $chipStyles?.borderRadius,
            color: $chipStyles?.textColor,
            fontSize: $chipStyles?.fontSize,
            fontWeight: $chipStyles?.fontWeight,
            letterSpacing: $chipStyles?.letterSpacing,
            textDecoration: $chipStyles?.textDecoration,
            textTransform: $chipStyles?.textTransform,
            "&:hover": {
                textDecoration: $chipStyles?.hoverTextDecoration,
                backgroundColor: $chipStyles.hoverBackgroundColor,
                borderColor: $chipStyles.hoverBorderColor,
            },
        }) as CSSObject
        return merge({
            display: "flex",
            color: theme.colours.plp.searchBanner.default.banner.chips.outlined.enabled.contrastTextColor,
            userSelect: "none",
            border: theme.colours.plp.searchBanner.chip.border,
            backgroundColor: theme.colours.plp.searchBanner.default.banner.chips.outlined.enabled.backgroundColor,
            borderColor: theme.colours.plp.searchBanner.default.banner.chips.outlined.enabled.borderColor,
            borderRadius: theme.colours.plp.searchBanner.chip.medium.borderRadius,
            padding: "0.313rem 0.563rem",
            textDecoration: theme.colours.plp.searchBanner.chip.textDecoration,
            fontSize: theme.colours.plp.searchBanner.chip.fontSize,
            fontWeight: theme.colours.plp.searchBanner.chip.fontWeight,
            letterSpacing: theme.colours.plp.searchBanner.chip.letterSpacing,
            lineHeight: theme.colours.plp.searchBanner.chip.lineHeight,
            justifyContent: "center",
            alignItems: "center",
            textTransform: $isStructuredBrowsingChip ? "capitalize" : theme.colours.plp.searchBanner.chip.textTransform,
            whiteSpace: "nowrap",
            "&:hover": {
                backgroundColor: theme.colours.plp.searchBanner.default.banner.chips.outlined.hovered.backgroundColor,
                borderColor: theme.colours.plp.searchBanner.default.banner.chips.outlined.hovered.borderColor,
            },
            ...($isConversational
                ? {
                      fontWeight: theme.colours.typography.body4.fontWeight,
                      fontFamily: `${theme.colours.typography.body3.fontFamily}, ${theme.colours.font.default}`,
                      letterSpacing: theme.colours.typography.body4.letterSpacing,
                      lineHeight: 1,
                      position: "relative",
                      padding: "0.625rem 1rem",
                      zIndex: 1,
                      textDecoration: "none",

                      "&::before": {
                          content: "''",
                          position: "absolute",
                          top: "-1px",
                          right: "-1px",
                          bottom: "-1px",
                          left: "-1px",
                          zIndex: -2,
                          background: "linear-gradient(45deg, #CA21F3, #1FA1F5, #CA21F3, #ff69b4, #1FA1F5)",
                          backgroundSize: "400%",
                          borderRadius: "2rem",
                          animation: "rotateGradient 3s linear infinite",
                      },

                      "&::after": {
                          content: "''",
                          position: "absolute",
                          zIndex: -1,
                          background: "white",
                          width: "calc(100% - 2px)",
                          height: "calc(100% - 2px)",
                          border: "1px solid #3511B8",
                          borderRadius: "2rem",
                      },

                      "@keyframes rotateGradient": {
                          "0%": {
                              backgroundPosition: "0% 50%",
                          },
                          "100%": {
                              backgroundPosition: "100% 50%",
                          },
                      },
                  }
                : {}),
            override,
        }) as CSSObject
    },
)

export const ChipContainer = styled(Typography, {shouldForwardProp: prop => !String(prop).startsWith("$")})<{
    $alignment: string | undefined
    $isIntelRecs: boolean
    $isStructuredBrowsingChip?: boolean
}>(
    ({
        theme,
        $alignment,
        $isIntelRecs,
        $isStructuredBrowsingChip = false,
    }: {
        theme: Theme
        $alignment: string | undefined
        $isIntelRecs
        $isStructuredBrowsingChip?: boolean
    }) => {
        // if we are trying to centre the chips we want to use marginInlineStart/End
        // as this will handle centre chips on mobile devices and prevents clipping
        // if not centre, we want to pass down the alignment to justifyContent to support spacing options
        const applyAlignment =
            typeof $alignment === "string" && $alignment.toLowerCase() === "center"
                ? {
                      "a:first-of-type": {
                          marginInlineStart: "auto",
                      },
                      "a:last-of-type": {
                          marginInlineEnd: "auto",
                      },
                  }
                : {
                      justifyContent: $alignment || "flex-start",
                  }
        const intelRecsStyles: CSSObject | undefined = $isIntelRecs
            ? {
                  justifyContent: "center",
                  [theme.breakpoints.down("md")]: {
                      justifyContent: "flex-start",
                      "a:first-of-type": {
                          marginInlineStart: "auto",
                      },
                      "a:last-of-type": {
                          marginInlineEnd: "auto",
                      },
                  },
              }
            : undefined
        return {
            display: "flex",
            alignItems: "flex-start",
            alignContent: "flex-start",
            justifyContent: $alignment || "flex-start",
            flexWrap: $isStructuredBrowsingChip ? "nowrap" : "wrap",
            overflow: $isStructuredBrowsingChip ? "auto" : "none",
            gap: "1rem",
            width: "100%",
            paddingRight: "1rem",
            paddingLeft: "1rem",
            paddingBottom: $isStructuredBrowsingChip ? "0.5rem" : "1rem",
            ...intelRecsStyles,
            [theme.breakpoints.down("md")]: {
                flexWrap: "nowrap",
                overflowY: "hidden",
                overflowX: "auto",
                paddingTop: "1px",
                justifyContent: "flex-start",
                ...applyAlignment,
                ...intelRecsStyles,
            },
            "&::-webkit-scrollbar": {
                display: "block",
                height: "1rem",
            },
            [theme.breakpoints.down("lg")]: {
                "&::-webkit-scrollbar": {
                    display: "none",
                },
            },

            "&::-webkit-scrollbar-button": {
                display: "none",
            },
            "&::-webkit-scrollbar-track": {
                borderRadius: "0rem 0rem 0.25rem 0rem",
                margin: "0 1rem",
            },
            "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#d9d9d9",
                borderRadius: "1rem",
                border: `0.3rem solid ${theme.colours.popover.header.backgroundColour}`,
            },
        }
    },
)

export const StyledConversationalHeading = styled(Typography)`
    font-weight: ${({theme}) => theme.colours.typography.body3.fontWeight};
    font-family: ${({theme}) => `${theme.colours.typography.body3.fontFamily}, ${theme.colours.font.default}`};
    letter-spacing: ${({theme}) => theme.colours.typography.body3.letterSpacing};
    line-height: ${({theme}) => theme.colours.typography.body3.lineHeight};
`

export const StyledConversationalImg = styled("img")`
    margin-right: 0.5rem;
    width: 1.25rem;
    height: 1.25rem;
`
export const StyledConversationalHeadingContainer = styled("div")`
    display: flex;
    align-self: center;
    margin-bottom: 1rem;
`
